import React, { useState } from 'react'
import { Avatar, DatePicker, Spin } from 'antd'
import moment from 'moment'
import CustomModal from 'components/global/CustomModal'
import { leagueAttendanceStore } from 'state/league/leagueAttendance'
import { leagueEventStore } from 'state/league/leagueEvent'
import {
  useGetLeagueRollCallHistory,
  RollCallHistoryItem,
} from 'api/league/LeagueAttendance/leagueAttendance'
import { getNameSignature } from 'helper/utility'
import rollCallIcon from 'assets/Images/lookout.svg'

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

const LeagueRollCallHistoryModal: React.FC = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment())

  const { rollCallHistoryModalOpen, setRollCallHistoryModalOpen } =
    leagueAttendanceStore(store => ({
      rollCallHistoryModalOpen: store.rollCallHistoryModalOpen,
      setRollCallHistoryModalOpen: store.setRollCallHistoryModalOpen,
    }))

  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId = leagueData?._id || ''

  const dateStr = selectedDate.startOf('day').toISOString()

  const { data, isLoading } = useGetLeagueRollCallHistory({
    leagueId,
    date: dateStr,
  })

  const rollCalls: RollCallHistoryItem[] = data || []

  const closeModal = () => {
    setRollCallHistoryModalOpen(false)
    setExpandedId(null)
  }

  return (
    <CustomModal
      openModal={rollCallHistoryModalOpen}
      closeModal={closeModal}
      title="Roll-call history"
      width={700}
      footer={false}
    >
      {/* Date picker */}
      <div className="flex-center mb-20px gap-10px">
        <span className="text-14 w-500">Date:</span>
        <DatePicker
          value={selectedDate}
          onChange={val => {
            if (val) {
              setSelectedDate(val)
              setExpandedId(null)
            }
          }}
          allowClear={false}
          className="selector"
          style={{ width: 180 }}
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-40px">
          <Spin />
        </div>
      ) : rollCalls.length === 0 ? (
        <p className="text-14 py-30px text-center" style={{ color: '#908F98' }}>
          No roll-calls found for this date
        </p>
      ) : (
        <div>
          {rollCalls.map(rc => {
            const isOpen = expandedId === rc._id
            return (
              <div key={rc._id} className="border-btm pb-15px mt-15px">
                {/* Header row */}
                <div className="flex-center justify-between">
                  <div className="flex-center gap-10px">
                    <img src={rollCallIcon} alt="roll-call" style={{ width: 28 }} />
                    <div>
                      <p className="text-14 w-600">{rc.name}</p>
                      <p className="text-12" style={{ color: '#908F98' }}>
                        {fmtTime(rc.time)} &middot; {rc.participants?.length ?? 0} participants
                      </p>
                    </div>
                  </div>

                  {(rc.participants?.length ?? 0) > 0 && (
                    <button
                      className="btn-borderless text-14 prim-txt"
                      onClick={() => setExpandedId(isOpen ? null : rc._id)}
                    >
                      {isOpen ? 'Close' : 'View'}
                    </button>
                  )}
                </div>

                {/* Expanded participants */}
                {isOpen && (
                  <div className="mt-15px ml-40px">
                    {rc.participants.map(p => (
                      <div key={p._id} className="flex-center gap-10px mb-10px">
                        <Avatar size={32}>
                          {getNameSignature(`${p.firstName || ''} ${p.lastName || ''}`)}
                        </Avatar>
                        <span className="text-14">
                          {`${p.firstName || ''} ${p.lastName || ''}`.trim() || '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </CustomModal>
  )
}

export default LeagueRollCallHistoryModal
